import * as React from 'react'
import { t } from 'ttag'
import { Card } from '../../components/Card/Card'
import NotificationsWrapper from '../../components/Notification/NotificationsWrapper'
import { PasswordPolicy, ResetPassword$Request, UserCompanyDto } from '../../services/api.types'
import YumSubmitButton from '../../components/YumSubmitButton/YumSubmitButton'
import YumInput from '../../components/YumInput/YumInput'
import YumPasswordPolicy from '../../components/YumPasswordPolicy/YumPasswordPolicy'
import { ErrorMessage } from '../../components/ErrorMessage/ErrorMessage'
import { isPasswordFieldErrorVisible } from '../../common/helpers/formHelpers'

import { ReactComponent as LockIcon } from '../../assets/v2/icons/lock.svg'
import { ReactComponent as LockFilledIcon } from '../../assets/v2/icons/lock-filled.svg'
import { ReactComponent as EmailFilledIcon } from '../../assets/v2/icons/email-filled.svg'
import { ReactComponent as ArrowBackIcon } from '../../assets/v3/icons/arrow-back.svg'
import { DesktopSidebar } from '../../components/DesktopSidebar/DesktopSidebar'
import Typography from '@material-ui/core/Typography'
import { IGlobalState } from '../../Root/store/appReducer'
import { store } from '../../index'
import { push } from 'connected-react-router'
import { ROUTES } from '../../common/Routes/routePaths'
import { INotification } from '../../common/notifications/NotificationReducers'

export interface IResetPasswordProps {
  authenticated: boolean
  resetPasswordInProgress: boolean
  email: string
  userCompanies: UserCompanyDto[]
}

export interface IResetPasswordDispatchProps {
  resetPassword(payload: ResetPassword$Request): void
  doLogOut(): void
}

export interface IForgotPasswordState {
  newPassword: string
  isFormSubmitted: boolean
  isPasswordPolicyValid: boolean
}

export class ResetPassword extends React.Component<
  IResetPasswordProps & IResetPasswordDispatchProps,
  IForgotPasswordState
> {
  state: IForgotPasswordState = { newPassword: '', isFormSubmitted: false, isPasswordPolicyValid: true }

  async UNSAFE_componentWillReceiveProps(nextProps: any) {
    console.log('UNSAFE_componentWillReceiveProps', nextProps)
    if (!nextProps.authenticated || !nextProps.resetPasswordInProgress) {
      const globalState = store.getState() as any as IGlobalState
      console.log('not authenticated, redirect to forgot password')
      // @ts-ignore
      if (globalState.router && globalState.router.location) {
        const notification: INotification = {
          type: 'warning',
          content: t`Invalid link. Please try again!`,
        }
        store.dispatch(push(ROUTES.FORGOT_PASSWORD.to, { notification }))
      }
    }
  }

  handleCancel = () => {
    this.props.doLogOut()
  }

  handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target
    this.setState({ newPassword: value })
  }

  onPolicyChange = (isPasswordPolicyValid: boolean) => {
    this.setState({ isPasswordPolicyValid })
  }

  getPasswordPolicy = () => {
    const { userCompanies } = this.props
    return (
      (userCompanies.find((uc: UserCompanyDto) => uc.passwordPolicy)?.passwordPolicy as PasswordPolicy) ||
      PasswordPolicy.DEFAULT
    )
  }

  getPasswordMinLength = () => {
    const { userCompanies } = this.props
    return (
      userCompanies.filter((uc) => uc.passwordMinLength).sort((a, b) => b.passwordMinLength - a.passwordMinLength)[0]
        ?.passwordMinLength || 6
    )
  }

  onSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    this.setState({ isFormSubmitted: true })

    const minLength = this.getPasswordMinLength()
    const lengthError = isPasswordFieldErrorVisible(this.state.newPassword, false, minLength)
    const hasErrors = lengthError || !this.state.isPasswordPolicyValid
    if (hasErrors) return

    this.props.resetPassword({ newPassword: this.state.newPassword })
  }

  render() {
    const { email } = this.props
    const { newPassword, isFormSubmitted, isPasswordPolicyValid } = this.state
    const minLength = this.getPasswordMinLength()
    const policy = this.getPasswordPolicy()
    const lengthError = isPasswordFieldErrorVisible(newPassword, false, minLength)
    const showLengthError = isFormSubmitted && lengthError
    const showPolicyError = isFormSubmitted && !lengthError && !isPasswordPolicyValid
    return (
      <Card className="login pt-12 reset-password">
        <div className="login__inner">
          <a className="yum-back" style={{ cursor: 'pointer', display: 'inline-block' }} onClick={this.handleCancel}>
            <ArrowBackIcon />
          </a>
          <NotificationsWrapper />
          <form className="sign-up__form" onSubmit={this.onSubmit}>
            <Typography className={'h1-desktop'} variant="h1" style={{ textAlign: 'center' }}>
              {t`Reset password`}
            </Typography>

            {email && (
              <YumInput
                fullWidth={true}
                label={t`Email`}
                type="email"
                value={email}
                disabled
                startIcon={EmailFilledIcon}
                style={{ marginBottom: '16px', opacity: 0.5 }}
              />
            )}

            <YumInput
              fullWidth={true}
              startIcon={LockFilledIcon}
              startIconInactive={LockIcon}
              label={t`New password`}
              type="password"
              name="newPassword"
              value={newPassword}
              onChange={this.handleInputChange}
              error={showLengthError || showPolicyError}
              style={{ marginBottom: 0 }}
            />
            {showLengthError && (
              <ErrorMessage>{t`Password must be at least 6 characters.`.replace('6', String(minLength))}</ErrorMessage>
            )}
            {policy !== PasswordPolicy.DEFAULT && (
              <div style={{ marginTop: 12 }}>
                <YumPasswordPolicy policy={policy} password={newPassword} onPolicyChange={this.onPolicyChange} />
              </div>
            )}
            <div style={{ marginTop: 16 }}>
              <YumSubmitButton text={t`Save`} />
            </div>
          </form>
        </div>
        <DesktopSidebar />
      </Card>
    )
  }
}
